// BL-402: the finale of the locked website film, drawn live on <canvas>. Ported from Appendix A
// `drawFinale()` (same math as therideatl-site/intro/intro.html). Not a video file.
import { easeInOut, easeOut, seg, HOLD_LINE_DELAY } from '../../lib/welcomeSequence';

export const DISPLAY_FONT = '"Exo 2"';
export const LOGO_SIZE = 900;
const SHARP = 0.3; // below this many pixels of blur, skip the filter entirely

// Performance (not part of the look): a canvas `filter: blur()` allocates a layer the size of the whole
// canvas, so the preview's per-letter blur cost ~1.3s per frame in software-rendered Chrome (a frozen
// frame on weaker devices). Blurring on small scratch canvases gives the same picture at a fraction of it.
const scratch = {};
function scratchCanvas(key, w, h) {
    const canvas = scratch[key] || (scratch[key] = document.createElement('canvas'));
    canvas.width = Math.max(1, Math.ceil(w)); canvas.height = Math.max(1, Math.ceil(h)); // resizing also clears
    return { canvas, g: canvas.getContext('2d') };
}

function chromeGradient(g, top, bottom) {
    const chrome = g.createLinearGradient(0, top, 0, bottom);
    chrome.addColorStop(0, '#ffffff'); chrome.addColorStop(0.45, '#d6dae0'); chrome.addColorStop(0.55, '#8c939c'); chrome.addColorStop(1, '#eef1f4');
    return chrome;
}

// One letter of "Welcome, Name…", blurred on a glyph-sized canvas; returns where its baseline-left sits.
function blurredGlyph(ch, { font, size, width, blur, shadow }) {
    const pad = Math.ceil(blur * 3 + shadow * 2 + 4);
    const { canvas, g } = scratchCanvas('glyph', width + pad * 2, size * 1.4 + pad * 2);
    if (!g) return null;
    const mid = canvas.height / 2;
    g.font = font; g.textBaseline = 'middle'; g.filter = `blur(${blur}px)`;
    g.shadowColor = 'rgba(214,218,224,.35)'; g.shadowBlur = shadow;
    g.fillStyle = chromeGradient(g, mid - size / 2, mid + size / 2);
    g.fillText(ch, pad, mid);
    return { canvas, ox: pad, oy: mid };
}

// The logo square, blurred at half resolution (the blur hides the difference) inside a margin.
function blurredLogo(lgC, sz, blur) {
    const scale = 0.5, margin = blur * 3;
    const { canvas, g } = scratchCanvas('logo', (sz + margin * 2) * scale, (sz + margin * 2) * scale);
    if (!g) return null;
    g.filter = `blur(${blur * scale}px)`;
    g.drawImage(lgC, margin * scale, margin * scale, sz * scale, sz * scale);
    return { canvas, margin };
}

// The logo (or, with no logo, the brand name in chrome lettering) on its own square canvas, so the sheen
// can be composited onto it alone.
function paintLogo(lg, logo, brandName) {
    lg.setTransform(1, 0, 0, 1, 0, 0);
    lg.globalCompositeOperation = 'source-over';
    lg.clearRect(0, 0, LOGO_SIZE, LOGO_SIZE);
    if (logo) {
        lg.drawImage(logo, 0, 0, LOGO_SIZE, LOGO_SIZE);
        return;
    }
    const text = brandName || 'IntelAgent';
    let size = 150;
    lg.font = `200 ${size}px ${DISPLAY_FONT}`;
    const width = lg.measureText(text).width;
    if (width > LOGO_SIZE * 0.9) { size *= (LOGO_SIZE * 0.9) / width; lg.font = `200 ${size}px ${DISPLAY_FONT}`; }
    lg.fillStyle = chromeGradient(lg, 450 - size / 2, 450 + size / 2); lg.textAlign = 'center'; lg.textBaseline = 'middle';
    lg.fillText(text, 450, 450);
}

export function drawFinale(ctx, { W, H, t, name, logo, brandName, lg, lgC, hold = 0, out = 0, sheen = true }) {
    const cx = W / 2, cy = H / 2;
    const u = Math.min(W / 1000, H / 720); // 1 film px
    ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.filter = 'none';
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H);

    // --- "Welcome, Name…"
    const wIn = seg(t, 9.5, 10.9), wOut = easeInOut(seg(t, 11.5, 12.1));
    if (name !== null && wIn > 0 && wOut < 1) {
        const word = name ? `Welcome, ${name}` : 'Welcome';
        let size = 96 * u;
        ctx.font = `200 ${size}px ${DISPLAY_FONT}`;
        const track = 10 * u, dotsW = 3 * 26 * u;
        const measure = () => [...word].reduce((a, ch) => a + ctx.measureText(ch).width, 0) + track * (word.length - 1) + 14 * u + dotsW;
        let total = measure();
        if (total > W * 0.86) { size *= (W * 0.86) / total; ctx.font = `200 ${size}px ${DISPLAY_FONT}`; total = measure(); }
        const k2 = size / (96 * u);
        ctx.save(); ctx.translate(cx, cy); ctx.transform(1, 0, -0.16, 1, 0, 0); ctx.textBaseline = 'middle';
        const widths = [...word].map((ch) => ctx.measureText(ch).width);
        let x = -total / 2;
        const chrome = chromeGradient(ctx, -size / 2, size / 2);
        const font = ctx.font;
        const step = Math.min(0.09, 0.9 / word.length); // longer names type a touch faster
        [...word].forEach((ch, i) => {
            const k = easeOut(seg(t, 9.5 + i * step, 10.25 + i * step)) * (1 - wOut);
            const blur = (1 - k) * 8 * u;
            ctx.save(); ctx.globalAlpha = k;
            ctx.translate(0, (1 - k) * 14 * u - wOut * 10 * u);
            const glyph = k > 0 && blur >= SHARP ? blurredGlyph(ch, { font, size, width: widths[i], blur, shadow: 18 * u }) : null;
            if (glyph) {
                ctx.drawImage(glyph.canvas, x - glyph.ox, -glyph.oy);
            } else if (k > 0) {
                ctx.shadowColor = 'rgba(214,218,224,.35)'; ctx.shadowBlur = 18 * u;
                ctx.fillStyle = chrome; ctx.fillText(ch, x, 0);
            }
            ctx.restore();
            x += widths[i] + track;
        });
        x += 14 * u;
        for (let d = 0; d < 3; d++) {
            const k = easeOut(seg(t, 10.5 + d * 0.22, 10.85 + d * 0.22)) * (1 - wOut);
            ctx.save(); ctx.globalAlpha = k; ctx.fillStyle = '#F0B429'; ctx.shadowColor = 'rgba(240,180,41,.7)'; ctx.shadowBlur = 14 * u;
            ctx.beginPath(); ctx.arc(x + (9 + d * 26) * u * k2, size * 0.3 - wOut * 10 * u, 5.5 * u * k2, 0, Math.PI * 2); ctx.fill(); ctx.restore();
        }
        const r = easeInOut(seg(t, 10.1, 11.1)) * (1 - wOut);
        // The preview built this gradient around 0 but filled it in screen space, so the rule never showed.
        // Built in screen space, it draws as the directive describes: a chrome rule under the name.
        const rule = ctx.createLinearGradient(cx - total / 2, 0, cx + total / 2, 0);
        rule.addColorStop(0, 'rgba(214,218,224,0)'); rule.addColorStop(0.5, 'rgba(214,218,224,.7)'); rule.addColorStop(1, 'rgba(214,218,224,0)');
        ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.fillStyle = rule; ctx.fillRect(cx - (total / 2) * r, cy + 70 * u * k2, total * r, 1.2 * u);
        ctx.restore();
    }

    // --- logo soft reveal + sheen (+ hold breathing, + exit fade)
    const L0 = easeOut(seg(t, 11.8, 13.4)), Lout = out;
    if (L0 > 0) {
        const breathe = hold ? 1 + Math.sin(hold * 2.2) * 0.006 : 1;
        const sz = 420 * u * (0.94 + 0.06 * L0) * breathe * (1 + Lout * 0.04);
        ctx.save(); ctx.globalCompositeOperation = 'lighter';
        const halo = ctx.createRadialGradient(cx, cy, sz * 0.3, cx, cy, sz * 0.85);
        halo.addColorStop(0, 'rgba(0,0,0,0)'); halo.addColorStop(0.62, `rgba(190,200,212,${0.16 * L0 * (1 - Lout)})`); halo.addColorStop(0.72, `rgba(3,183,201,${0.07 * L0 * (1 - Lout)})`); halo.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = halo; ctx.fillRect(cx - sz * 0.85, cy - sz * 0.85, sz * 1.7, sz * 1.7); ctx.restore(); // transparent beyond its radius
        paintLogo(lg, logo, brandName);
        const sh = seg(t, 13.1, 14.0);
        if (sheen && sh > 0 && sh < 1) {
            lg.globalCompositeOperation = 'source-atop';
            const sx = -300 + sh * 1500, band = lg.createLinearGradient(sx - 160, 0, sx + 160, 0);
            band.addColorStop(0, 'rgba(255,255,255,0)'); band.addColorStop(0.5, 'rgba(170,232,242,.6)'); band.addColorStop(1, 'rgba(255,255,255,0)');
            lg.save(); lg.translate(450, 450); lg.rotate(-0.35); lg.translate(-450, -450); lg.fillStyle = band; lg.fillRect(-400, -400, 1700, 1700); lg.restore();
        }
        const logoBlur = (1 - L0) * 16 * u;
        const soft = logoBlur >= SHARP ? blurredLogo(lgC, sz, logoBlur) : null;
        ctx.save(); ctx.globalAlpha = L0 * (1 - Lout);
        if (soft) ctx.drawImage(soft.canvas, cx - sz / 2 - soft.margin, cy - sz / 2 - soft.margin, sz + soft.margin * 2, sz + soft.margin * 2);
        else ctx.drawImage(lgC, cx - sz / 2, cy - sz / 2, sz, sz);
        ctx.restore();

        // Honest wait indicator: only appears once we're holding for the app.
        if (hold > HOLD_LINE_DELAY) {
            const a = Math.min(1, (hold - HOLD_LINE_DELAY) / 0.4) * (1 - Lout), bw = 160 * u, by = cy + sz / 2 + 34 * u;
            ctx.save(); ctx.globalAlpha = a;
            ctx.fillStyle = 'rgba(214,218,224,.12)'; ctx.fillRect(cx - bw / 2, by, bw, 1.5 * u);
            const p = (hold * 0.6) % 1, g = ctx.createLinearGradient(cx - bw / 2 + p * bw - 40 * u, 0, cx - bw / 2 + p * bw + 40 * u, 0);
            g.addColorStop(0, 'rgba(3,183,201,0)'); g.addColorStop(0.5, 'rgba(3,183,201,.9)'); g.addColorStop(1, 'rgba(3,183,201,0)');
            ctx.fillStyle = g; ctx.fillRect(cx - bw / 2, by, bw, 1.5 * u);
            ctx.font = `400 ${13 * u}px ${DISPLAY_FONT}, sans-serif`; ctx.textAlign = 'center'; ctx.fillStyle = 'rgba(140,147,156,.95)';
            ctx.fillText('Getting your Dispatcher ready', cx, by + 26 * u);
            ctx.restore();
        }
    }
    const v = ctx.createRadialGradient(cx, cy, H * 0.35, cx, cy, Math.max(W, H) * 0.8);
    v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,.6)');
    ctx.fillStyle = v; ctx.fillRect(0, 0, W, H);
}
